import type { PackageProviderName } from 'data/ppoints/constants';
import { SettingKey } from 'data/ppoints/constants';
import useEcoms from 'hooks/useEcoms';
import useSettings from 'hooks/useSettings';
import { useShippingZones } from 'hooks/useShippingZones';
import type { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { ShopifyShippingZone } from 'models/ppoints/ShopifyShippingZone';
import { useMemo } from 'react';
import CountryService from 'services/settings/ppoints/CountryService';

export type MappedZone = {
    id: number;
    zone: ShopifyShippingZone;
    providers: PackageProviderName[];
};

export type ZonelessCountry = {
    name: string;
    /** The country name as it is stored in the provider_to_country setting — what the create-zone API expects. */
    rawName: string;
    providers: PackageProviderName[];
};

const normaliseCountryName = (name: string): string => CountryService.getCountryNameFromShopifyCountryName(name ?? '');

const useZonesWithMappedProviders = () => {
    const ecoms = useEcoms();
    const shippingZones = useShippingZones(ecoms.id || null);
    const { settings: providerToCountry, fetching: fetchingSettings } = useSettings<ProviderToCountrySetting>(
        SettingKey.provider_to_country
    );

    const providersByCountry = useMemo(() => {
        const byCountry = new Map<string, { rawName: string; providers: PackageProviderName[] }>();

        Object.entries(providerToCountry ?? {}).forEach(([country, providers]) => {
            if (!providers?.length) return;
            byCountry.set(normaliseCountryName(country), { rawName: country, providers });
        });

        return byCountry;
    }, [providerToCountry]);

    const { zones, countriesWithoutZone } = useMemo(() => {
        const matched = new Set<string>();

        const mappedZones: MappedZone[] = (shippingZones.data ?? [])
            .map((zone) => {
                const providers = new Set<PackageProviderName>();

                zone.countries.forEach((country) => {
                    const name = normaliseCountryName(country.name);
                    const countryProviders = providersByCountry.get(name);
                    if (!countryProviders) return;

                    matched.add(name);
                    countryProviders.providers.forEach((provider) => providers.add(provider));
                });

                return { id: zone.id, zone, providers: [...providers] };
            })
            .filter((zone) => zone.providers.length > 0);

        const zoneless: ZonelessCountry[] = [...providersByCountry.entries()]
            .filter(([name]) => !matched.has(name))
            .map(([name, { rawName, providers }]) => ({ name, rawName, providers }));

        return { zones: mappedZones, countriesWithoutZone: zoneless };
    }, [shippingZones.data, providersByCountry]);

    return {
        zones,
        countriesWithoutZone,
        isLoading: shippingZones.isInitialLoading || fetchingSettings,
        refetchZones: shippingZones.refetch,
    };
};

export default useZonesWithMappedProviders;
